/**
 * The type of (redux) action which records the moderator's prejoin choice to
 * start the meeting in backstage. It is applied (switching into the backstage
 * room) once the local user is confirmed moderator after joining.
 *
 * {
 *     type: SET_START_BACKSTAGE,
 *     enabled: boolean
 * }
 */
export const SET_START_BACKSTAGE = 'SET_START_BACKSTAGE';
/**
 * The type of (redux) action which records the moderator's prejoin choice to
 * join hidden, together with the roles that may still see them. The payload is
 * the audience rather than a boolean so a future UI can offer more than
 * "moderators only" without another action.
 *
 * {
 *     type: SET_JOIN_HIDDEN,
 *     audience: ?string
 * }
 */
export const SET_JOIN_HIDDEN = 'SET_JOIN_HIDDEN';
/**
 * Updates the visitor-facing webinar layout for the current room.
 *
 * {
 *     type: SET_AUDIENCE_LAYOUT,
 *     roomName: string,
 *     showOnlyPanelists: boolean,
 *     stageOnlyMode: boolean
 * }
 */
export const SET_AUDIENCE_LAYOUT = 'SET_AUDIENCE_LAYOUT';
/**
 * The type of (redux) action which records the room's confirmed local-recording
 * policy, read from room metadata {@code localRecording.allowed}. Server truth,
 * never set optimistically.
 *
 * {
 *     type: SET_LOCAL_RECORDING_ALLOWED,
 *     allowed: boolean | undefined
 * }
 */
export const SET_LOCAL_RECORDING_ALLOWED = 'SET_LOCAL_RECORDING_ALLOWED';
/**
 * The type of (redux) action which records that a backstage/main room switch is
 * in flight, so the room-switch screen can name the room being joined instead of
 * borrowing the breakout-rooms wording.
 *
 * {
 *     type: SET_ROOM_SWITCH_TARGET,
 *     target: 'backstage' | 'main' | undefined
 * }
 */
export const SET_ROOM_SWITCH_TARGET = 'SET_ROOM_SWITCH_TARGET';
/**
 * The type of (redux) action which stores the server-published list of people
 * currently in the backstage room, for the moderator's promote controls.
 *
 * {
 *     type: SET_BACKSTAGE_ROSTER,
 *     occupants: Array<{ id: string, name?: string, role?: string }>
 * }
 */
export const SET_BACKSTAGE_ROSTER = 'SET_BACKSTAGE_ROSTER';
/**
 * The type of (redux) action which stores the server-published list of people
 * currently in the MAIN room, for a moderator who is sitting backstage. The
 * mirror of SET_BACKSTAGE_ROSTER, and it carries more per-occupant state: the
 * pane rendering it has no participant list of its own to read mute or hidden
 * status from, because those people are in a room this client is not in.
 *
 * The roster push also carries the main room's room-wide policies, so the
 * switches a backstage moderator sees read the state the audience is under.
 *
 * {
 *     type: SET_MAIN_ROOM_ROSTER,
 *     occupants: Array<IWebinarRosterOccupant>,
 *     policy?: IMainRoomPolicy
 * }
 */
export const SET_MAIN_ROOM_ROSTER = 'SET_MAIN_ROOM_ROSTER';
/**
 * Records the room's "block everyone's mic and camera" flag as read from room
 * metadata.
 *
 * {
 *     type: SET_MEDIA_BLOCK_ALL,
 *     enabled: boolean | undefined
 * }
 */
export const SET_MEDIA_BLOCK_ALL = 'SET_MEDIA_BLOCK_ALL';
